import { useState } from 'react';
import { ADDONS, formatStorage, PLANS, type AddonId, type LimitKey } from '@machs/shared';
import { api } from '../lib/api';
import { usePlan } from '../lib/plan';
import { Icon } from '../lib/icons';
import { useToast } from './ui';

const METERS: { key: Exclude<LimitKey, 'videoSeconds' | 'boardsPerProject'>; label: string }[] = [
  { key: 'storageMb', label: 'Speicher' },
  { key: 'users', label: 'Mitglieder' },
  { key: 'projects', label: 'Projekte' },
  { key: 'openTasks', label: 'Offene Aufgaben' },
  { key: 'activeShares', label: 'Aktive Freigaben' },
  { key: 'businesses', label: 'Geschäfte' },
];

/** Tarif, Testzeitraum, Nutzung an den Grenzen und Verwaltung des Abos. */
export function PlanSection() {
  const { info, showUpgrade, refresh } = usePlan();
  const toast = useToast();
  const [busy, setBusy] = useState<string | null>(null);
  if (!info) return null;

  const fmt = (key: LimitKey, v: number) => (key === 'storageMb' ? formatStorage(v) : String(v));

  async function portal() {
    setBusy('portal');
    try {
      const r = await api<{ url: string }>('/billing/portal', { method: 'POST' });
      location.href = r.url;
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Hat nicht geklappt');
    } finally {
      setBusy(null);
    }
  }

  async function addon(id: AddonId, active: boolean) {
    setBusy(id);
    try {
      await api('/billing/addon', { method: 'POST', json: { addon: id, active } });
      toast(active ? `${ADDONS[id].name} ist gebucht` : `${ADDONS[id].name} ist abbestellt`);
      refresh();
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Hat nicht geklappt');
    } finally {
      setBusy(null);
    }
  }

  const trialDate = info.trialEndsAt ? new Date(info.trialEndsAt).toLocaleDateString('de-DE', { day: 'numeric', month: 'long' }) : null;
  return (
    <section className="flex flex-col gap-3 rounded-2xl border border-[var(--line)] bg-[var(--surface)] p-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-0 flex-1">
          <h3 className="m-0 text-[14px] font-semibold">Tarif und Nutzung</h3>
          <p className="m-0 mt-0.5 text-[13px] text-[var(--muted)]">
            {info.trial
              ? `Ihr testet ${PLANS[info.plan].name} bis ${trialDate}. Danach gilt Free, alle Daten bleiben erhalten.`
              : `Tarif ${PLANS[info.plan].name}${info.interval ? (info.interval === 'year' ? ', jährlich' : ', monatlich') : ''}${info.extraSeats ? `, ${info.extraSeats} zusätzliche Mitglieder` : ''}.`}
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => showUpgrade()}><Icon name="sparkle" size={16} />Tarife vergleichen</button>
        {info.hasSubscription && info.billing.canManage && (
          <button className="btn btn-ghost" disabled={busy === 'portal'} onClick={() => void portal()}>Abo und Rechnungen</button>
        )}
      </div>
      <div className="meters">
        {METERS.map(({ key, label }) => {
          const max = info.limits[key];
          const used = info.usage[key];
          const share = max === null ? 0 : Math.min(1, used / max);
          return (
            <div key={key} className={`meter ${share >= 1 ? 'full' : share >= 0.8 ? 'warn' : ''}`}>
              <div className="flex justify-between gap-2 text-[12.5px]">
                <span className="text-[var(--muted)]">{label}</span>
                <b className="font-semibold">{fmt(key, used)}{max === null ? '' : ` von ${fmt(key, max)}`}</b>
              </div>
              <span className="track"><span className="fill" style={{ width: max === null ? '0%' : `${Math.max(share * 100, used > 0 ? 2 : 0)}%` }} /></span>
            </div>
          );
        })}
      </div>
      {info.paidPlan !== 'free' && info.billing.canManage && (
        <div className="flex flex-col gap-2 border-t border-[var(--line)] pt-3">
          <h4 className="m-0 text-[13px] font-semibold text-[var(--muted)]">Add-ons</h4>
          {(Object.keys(ADDONS) as AddonId[]).map((id) => {
            const a = ADDONS[id];
            if (a.feature && PLANS[info.paidPlan].features.includes(a.feature) && !info.addons.includes(id)) return null;
            const on = info.addons.includes(id);
            return (
              <div key={id} className="flex items-center gap-3">
                <span className="min-w-0 flex-1">
                  <b className="block text-[14px] font-medium">{a.name} · {a.price} € im Monat</b>
                  <small className="text-[12.5px] text-[var(--muted)]">{a.description}</small>
                </span>
                <button className={`switch ${on ? 'on' : ''}`} role="switch" aria-checked={on} aria-label={a.name} disabled={busy === id || !info.billing.ready} onClick={() => void addon(id, !on)} />
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
